import { createContext, createElement, useContext, useRef, forwardRef } from 'react';
import { isValidElementType } from 'react-is';

var GlitzContext = createContext(undefined);
var ThemeContext = createContext(undefined);

var Export = function GlitzProvider(props) {
    return createElement(GlitzContext.Provider, { value: props.glitz }, props.children);
};

var Export$1 = function ThemeProvider(props) {
    return createElement(ThemeContext.Provider, { value: props.theme }, props.children);
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var SECRET_COMPOSE = '$$GLITZ1';
var SECRET_TYPE = '$$GLITZ2';
var SECRET_DECORATOR = '$$GLITZ3';

var Type;
(function (Type) {
    Type[Type["Element"] = 0] = "Element";
    Type[Type["ElementLike"] = 1] = "ElementLike";
})(Type || (Type = {}));
function isType(type) {
    return SECRET_TYPE in type;
}
function createElementType(tag) {
    var _a;
    return _a = {},
        _a[SECRET_TYPE] = Type.Element,
        _a.value = tag,
        _a;
}
function isElementType(type) {
    return isType(type) && type[SECRET_TYPE] === Type.Element;
}
function createPredefined(tag) {
    return function (style) { return factory(createElementType(tag), [style]); };
}
function assignPredefined(target) {
    for (var _i = 0, predefinedElements_1 = predefinedElements; _i < predefinedElements_1.length; _i++) {
        var tag = predefinedElements_1[_i];
        target[tag[0].toUpperCase() + tag.slice(1)] = factory(createElementType(tag), []);
        target[tag] = createPredefined(tag);
    }
    return target;
}
var predefinedElements = [
    'a',
    'abbr',
    'address',
    'area',
    'article',
    'aside',
    'audio',
    'b',
    'base',
    'bdi',
    'bdo',
    'big',
    'blockquote',
    'body',
    'br',
    'button',
    'canvas',
    'caption',
    'cite',
    'code',
    'col',
    'colgroup',
    'data',
    'datalist',
    'dd',
    'del',
    'details',
    'dfn',
    'dialog',
    'div',
    'dl',
    'dt',
    'em',
    'embed',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'head',
    'header',
    'hgroup',
    'hr',
    'html',
    'i',
    'iframe',
    'img',
    'input',
    'ins',
    'kbd',
    'keygen',
    'label',
    'legend',
    'li',
    'link',
    'main',
    'map',
    'mark',
    'menu',
    'menuitem',
    'meta',
    'meter',
    'nav',
    'noscript',
    'object',
    'ol',
    'optgroup',
    'option',
    'output',
    'p',
    'param',
    'picture',
    'pre',
    'progress',
    'q',
    'rp',
    'rt',
    'ruby',
    's',
    'samp',
    'script',
    'section',
    'select',
    'small',
    'source',
    'span',
    'strong',
    'style',
    'sub',
    'summary',
    'sup',
    'table',
    'tbody',
    'td',
    'textarea',
    'tfoot',
    'th',
    'thead',
    'time',
    'title',
    'tr',
    'track',
    'u',
    'ul',
    'var',
    'video',
    'wbr',
    'webview',
    'animate',
    'circle',
    'clipPath',
    'defs',
    'desc',
    'ellipse',
    'feBlend',
    'feColorMatrix',
    'feComponentTransfer',
    'feComposite',
    'feConvolveMatrix',
    'feDiffuseLighting',
    'feDisplacementMap',
    'feDistantLight',
    'feDropShadow',
    'feFlood',
    'feFuncA',
    'feFuncB',
    'feFuncG',
    'feFuncR',
    'feGaussianBlur',
    'feImage',
    'feMerge',
    'feMergeNode',
    'feMorphology',
    'feOffset',
    'fePointLight',
    'feSpecularLighting',
    'feSpotLight',
    'feTile',
    'feTurbulence',
    'filter',
    'foreignObject',
    'g',
    'image',
    'line',
    'linearGradient',
    'marker',
    'mask',
    'metadata',
    'path',
    'pattern',
    'polygon',
    'polyline',
    'radialGradient',
    'rect',
    'stop',
    'svg',
    'switch',
    'symbol',
    'text',
    'textPath',
    'tspan',
    'use',
    'view',
];

function applyClassName(component) {
    var _a;
    return _a = {},
        _a[SECRET_TYPE] = Type.ElementLike,
        _a.value = component,
        _a;
}
function isElementLikeType(type) {
    return isType(type) && type[SECRET_TYPE] === Type.ElementLike;
}

function useGlitz(inputStyles) {
    var glitz = useContext(GlitzContext);
    if (!glitz) {
        throw new Error("The `<GlitzProvider>` doesn't seem to be used correctly because the core instance couldn't be found");
    }
    var theme = useContext(ThemeContext);
    var finalStyles = styleToArray(inputStyles);
    var hasWarnedCacheInvalidationsRef = useRef(false);
    var totalCacheInvalidationsRef = useRef(0);
    var lastGlitzRef = useRef(glitz);
    var lastThemeRef = useRef(theme);
    var lastFinalStylesRef = useRef(finalStyles);
    var lastClassNamesRef = useRef();
    var applyRef = useRef();
    var isValidGlitz = lastGlitzRef.current === glitz;
    var isValidTheme = lastThemeRef.current === theme;
    var isValidStyle = shallowEquals(lastFinalStylesRef.current, finalStyles);
    lastGlitzRef.current = glitz;
    lastThemeRef.current = theme;
    lastFinalStylesRef.current = finalStyles;
    if (!applyRef.current || !isValidGlitz || !isValidTheme || !isValidStyle) {
        applyRef.current = function () {
            if (!finalStyles) {
                return;
            }
            if (isValidGlitz && isValidTheme && isValidStyle && typeof lastClassNamesRef.current === 'string') {
                return lastClassNamesRef.current || void 0;
            }
            if (process.env.NODE_ENV !== 'production') {
                if (typeof requestAnimationFrame === 'function' && !hasWarnedCacheInvalidationsRef.current) {
                    totalCacheInvalidationsRef.current++;
                    var currentCacheInvalidations_1 = totalCacheInvalidationsRef.current;
                    // Jump two frames to reset counter if there hasn't been any more renders with cache invalidation
                    requestAnimationFrame(function () {
                        requestAnimationFrame(function () {
                            if (totalCacheInvalidationsRef.current === currentCacheInvalidations_1) {
                                if (currentCacheInvalidations_1 > 3) {
                                    console.warn("Multiple re-renders of a styled component with invalidated cache was detected. Either make sure it doesn't re-render or make sure the cache is intact. More info: https://git.io/fxYyd");
                                    hasWarnedCacheInvalidationsRef.current = true;
                                }
                                totalCacheInvalidationsRef.current = 0;
                            }
                        });
                    });
                }
            }
            return (lastClassNamesRef.current = glitz.injectStyle(finalStyles, theme)) || void 0;
        };
    }
    var composeRef = useRef();
    if (!composeRef.current || !isValidStyle) {
        composeRef.current = function (defaults) { return styleToArray(defaults, finalStyles); };
    }
    return [applyRef.current, composeRef.current];
}
function styleToArray() {
    var dirtyStyles = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        dirtyStyles[_i] = arguments[_i];
    }
    var styles = [];
    for (var _a = 0, dirtyStyles_1 = dirtyStyles; _a < dirtyStyles_1.length; _a++) {
        var style = dirtyStyles_1[_a];
        if (!style) {
            continue;
        }
        if (typeof style === 'function') {
            style = style();
        }
        if (Array.isArray(style)) {
            for (var _b = 0, style_1 = style; _b < style_1.length; _b++) {
                var entry = style_1[_b];
                styles.push(entry);
            }
        }
        else {
            styles.push(style);
        }
    }
    return styles;
}
function shallowEquals(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    for (var i in a) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

function create(type, statics) {
    return isStyledComponent(type) ? type[SECRET_COMPOSE](statics) : factory(type, statics);
}
function factory(type, statics) {
    var _a;
    var Styled = Object.assign(forwardRef(function (props, ref) {
        var dynamics = props.css, restProps = __rest(props, ["css"]);
        var _a = useGlitz(styleToArray(statics, dynamics)), apply = _a[0], compose = _a[1];
        if (isElementType(type) || isElementLikeType(type)) {
            return createElement(type.value, __assign(__assign({}, restProps), { className: combineClassNames(props.className, apply()), ref: ref }));
        }
        if (isStyledComponent(type)) {
            return createElement(type, __assign(__assign({}, restProps), { css: compose(), ref: ref }));
        }
        return createElement(type, __assign(__assign({}, restProps), { compose: compose, ref: ref }));
    }), (_a = {},
        _a[SECRET_COMPOSE] = function (additionals) {
            return factory(type, additionals ? statics.concat(additionals) : statics);
        },
        _a));
    if (process.env.NODE_ENV !== 'production') {
        var inner = isElementType(type) || isElementLikeType(type) ? type.value : type;
        Styled.displayName = "Styled(" + (typeof inner === 'string' ? inner : inner.displayName || inner.name || 'Unknown') + ")";
    }
    return Styled;
}
function combineClassNames(a, b) {
    return a && b ? a + " " + b : a ? a : b;
}
function isStyledComponent(type) {
    return SECRET_COMPOSE in type;
}

function decorator(preStyle) {
    var fn = (function (arg1, arg2) {
        if (arg1) {
            if (isStyle(arg1)) {
                return decorator(preStyle.concat(arg1));
            }
            if (isDecorator(arg1)) {
                return decorator(preStyle.concat(arg1()));
            }
            return create(arg1, arg2 ? preStyle.concat(arg2) : preStyle);
        }
        return preStyle;
    });
    fn[SECRET_DECORATOR] = true;
    return fn;
}
function isDecorator(value) {
    return SECRET_DECORATOR in value;
}

function custom(arg1, arg2) {
    return isStyle(arg1) ? decorator([arg1]) : create(arg1, arg2 ? [arg2] : []);
}
var styledCustom = custom;
function isStyle(arg) {
    return typeof arg === 'object' && !isType(arg) && !isStyledComponent(arg) && !isValidElementType(arg);
}

function useStyle(styles) {
    var apply = useGlitz(styles)[0];
    return apply();
}

var styled = assignPredefined(styledCustom);

export { Export as GlitzProvider, Export$1 as ThemeProvider, applyClassName, isElementLikeType, styled, useStyle };
